import { useState, useEffect } from 'react';
import { IonMenu, IonHeader, IonToolbar, IonTitle, IonContent, IonList, IonItem, IonIcon, IonMenuToggle } from '@ionic/react';
import { useLocation } from 'react-router-dom';
import {
  homeOutline,
  businessOutline,
  listOutline,
  addCircleOutline,
  settingsOutline,
  personOutline,
  logInOutline,
  personAddOutline,
  keyOutline,
  logOutOutline
} from 'ionicons/icons';
import axios from 'axios';
import API_CONFIG from './config/api';
import './Menu.css';

export default function SideMenu({ refresh }) {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const location = useLocation();

  const closeMenu = () => {
    const menu = document.querySelector("ion-menu");
    if (menu) menu.close();
  };

  // Check if a route is active
  const isActive = (path) => {
    return location.pathname === path;
  };

  useEffect(() => {
    // Check login status
    const checkAuth = () => {
      const userId = sessionStorage.getItem('user_id');
      if (userId) {
        setIsLoggedIn(true);
        // Get user role from localStorage or sessionStorage (fallback)
        let roleId = localStorage.getItem('roleId') || sessionStorage.getItem('role_id');
        if (!roleId) {
          // fallback: fetch user info from backend
          // Try backend fallback only if accessible; many backend endpoints require server-side auth
          axios.get(`${API_CONFIG.MANAGE_USERS}?user_id=${userId}`)
            .then(resp => {
              if (resp && resp.data && resp.data.user) {
                const r = resp.data.user.role_id || resp.data.user.roleId || resp.data.user.role;
                if (r) {
                  roleId = String(r);
                  // persist to storage for future renders
                  sessionStorage.setItem('role_id', roleId);
                  localStorage.setItem('roleId', roleId);
                  setUserRole(roleId);
                  console.log('Fetched and set role in menu from backend:', roleId);
                } else {
                  console.warn('Could not determine role from backend response', resp.data.user);
                  setUserRole(null);
                }
              } else {
                console.warn('Unexpected manage_users response', resp);
                setUserRole(null);
              }
            })
            .catch(err => {
              console.warn('Failed to fetch user role for menu fallback (likely requires server session). Using optimistic org fallback if organization present.');
              // Optimistic fallback: if the user belongs to an organization (client-side), assume org admin menu
              const orgId = localStorage.getItem('organizationId') || sessionStorage.getItem('organization_id');
              if (orgId) {
                console.log('Optimistic fallback: found organizationId in storage, showing Organization Panel until user re-logins to confirm role. organizationId=', orgId);
                setUserRole('2'); // optimistic display only
              } else {
                setUserRole(null);
              }
            });
        } else {
          console.log('User role in menu (from storage):', roleId, 'User ID:', userId);
          setUserRole(roleId);
        }
      } else {
        setIsLoggedIn(false);
        setUserRole(null);
      }
    };

    checkAuth();

    // Also check when refresh prop changes
    if (refresh) {
      checkAuth();
    }
  }, [refresh]);
  return (
    <IonMenu contentId="main" menuId="main-menu" className="modern-menu">
      <IonHeader>
        <IonToolbar className="menu-header-toolbar">
          <div className="menu-title-wrapper">
            <div className="menu-title-container">
              <img
                className="menu-logo"
                src="/assets/final_peoppoll_logo.png"
                alt="logo"
              />
              <span className="menu-title-text">
                {/*  Peopoll :: <span className="text-slate-500">
                    {userRole === '1' ? 'INTEL' : (sessionStorage.getItem('organization_name') || 'ORG')}
                  </span> */}
              </span>
            </div>
          </div>
        </IonToolbar>
      </IonHeader>
      <IonContent className="menu-content">
        <IonList className="menu-list">
          {/* Role-specific poll views */}

          {/* Super Admin Dashboard Link */}
          {isLoggedIn && userRole === '1' && (
            <IonMenuToggle auto-hide="false">
              <IonItem
                routerLink="/super-admin"
                onClick={closeMenu}
                className={`menu-item admin-item ${isActive('/super-admin') ? 'item-active' : ''}`}
              >
                <IonIcon icon={homeOutline} slot="start" className="menu-item-icon" />
                <span className="menu-item-label">Dashboard</span>
              </IonItem>
            </IonMenuToggle>
          )}

          {isLoggedIn && (userRole === '2' || userRole === '4') && (
            <IonMenuToggle auto-hide="false">
              <IonItem
                routerLink="/page/view?filter=org"
                onClick={closeMenu}
                className={`menu-item ${isActive('/page/view') && location.search.includes('filter=org') ? 'item-active' : ''}`}
              >
                <IonIcon icon={businessOutline} slot="start" className="menu-item-icon" />
                <span className="menu-item-label">View My ORG Polls</span>
              </IonItem>
            </IonMenuToggle>
          )}

          {isLoggedIn && (userRole === '1' || userRole === '2' || userRole === '4') && (
            <IonMenuToggle auto-hide="false">
              <IonItem
                routerLink="/page/view?filter=general"
                onClick={closeMenu}
                className={`menu-item ${isActive('/page/view') && location.search.includes('filter=general') ? 'item-active' : ''}`}
              >
                <IonIcon icon={listOutline} slot="start" className="menu-item-icon" />
                <span className="menu-item-label">View General Polls</span>
              </IonItem>
            </IonMenuToggle>
          )}
          {isLoggedIn && (userRole === '1' || userRole === '2') && (
            <IonMenuToggle auto-hide="false">
              <IonItem
                routerLink="/page/add"
                onClick={closeMenu}
                className={`menu-item ${isActive('/page/add') ? 'item-active' : ''}`}
              >
                <IonIcon icon={addCircleOutline} slot="start" className="menu-item-icon" />
                <span className="menu-item-label">Add Poll</span>
              </IonItem>
            </IonMenuToggle>)}
          {/* Super Admin Panel - Only for role_id = 1 */}
          {isLoggedIn && userRole === '1' && (
            <IonMenuToggle auto-hide="false">
              <IonItem
                routerLink="/admin"
                onClick={closeMenu}
                className={`menu-item admin-item ${isActive('/admin') ? 'item-active' : ''}`}
              >
                <IonIcon icon={settingsOutline} slot="start" className="menu-item-icon" />
                <span className="menu-item-label">Super Admin Panel</span>
              </IonItem>
            </IonMenuToggle>)}
          {/* Organization Admin Panel - For role_id = 2 */}
          {isLoggedIn && userRole === '2' && (
            <IonMenuToggle auto-hide="false">
              <IonItem
                routerLink="/org-admin"
                onClick={closeMenu}
                className={`menu-item admin-item ${isActive('/org-admin') ? 'item-active' : ''}`}
              >
                <IonIcon icon={businessOutline} slot="start" className="menu-item-icon" />
                <span className="menu-item-label">Organization Panel</span>
              </IonItem>
            </IonMenuToggle>)}
          {/* User Dashboard - For regular users (role_id = 4) */}
          {isLoggedIn && userRole === '4' && (
            <IonMenuToggle auto-hide="false">
              <IonItem
                routerLink="/user-dashboard"
                onClick={closeMenu}
                className={`menu-item ${isActive('/user-dashboard') ? 'item-active' : ''}`}
              >
                <IonIcon icon={personOutline} slot="start" className="menu-item-icon" />
                <span className="menu-item-label">My Dashboard</span>
              </IonItem>
            </IonMenuToggle>)}
          {!isLoggedIn && (<IonMenuToggle auto-hide="false">
            <IonItem
              routerLink="/page/login"
              onClick={closeMenu}
              className={`menu-item login-item ${isActive('/page/login') ? 'item-active' : ''}`}
            >
              <IonIcon icon={logInOutline} slot="start" className="menu-item-icon" />
              <span className="menu-item-label">Login</span>
            </IonItem>
          </IonMenuToggle>)}
          {!isLoggedIn && (<IonMenuToggle auto-hide="false">
            <IonItem
              routerLink="/page/register"
              onClick={closeMenu}
              className={`menu-item register-item ${isActive('/page/register') ? 'item-active' : ''}`}
            >
              <IonIcon icon={personAddOutline} slot="start" className="menu-item-icon" />
              <span className="menu-item-label">Register</span>
            </IonItem>
          </IonMenuToggle>)}
          {!isLoggedIn && (<IonMenuToggle auto-hide="false">
            <IonItem
              routerLink="/page/reset-password"
              onClick={closeMenu}
              className={`menu-item reset-item ${isActive('/page/reset-password') ? 'item-active' : ''}`}
            >
              <IonIcon icon={keyOutline} slot="start" className="menu-item-icon" />
              <span className="menu-item-label">Reset Password</span>
            </IonItem>
          </IonMenuToggle>)}
          {isLoggedIn && (<IonMenuToggle auto-hide="false">
            <IonItem
              routerLink="/page/logout"
              onClick={closeMenu}
              className={`menu-item logout-item ${isActive('/page/logout') ? 'item-active' : ''}`}
            >
              <IonIcon icon={logOutOutline} slot="start" className="menu-item-icon" />
              <span className="menu-item-label">Logout</span>
            </IonItem>
          </IonMenuToggle>)}
        </IonList>
      </IonContent>
    </IonMenu>
  );
}

